import { useRef, useEffect, useState } from "react";
import { Color } from "three";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import LightWithFade from "./LightWithFade";

export default function VideoMesh({
  url,
  position = [0, 0, 0],
  scale = [1, 1, 1],
  active = false,
}) {
  const meshRef = useRef();

  // --- Vidéo ---
  const [video] = useState(() => {
    const vid = document.createElement("video");
    vid.src = url;
    vid.crossOrigin = "Anonymous";
    vid.loop = true;
    vid.muted = true;
    vid.play();
    return vid;
  });

  const colors1 = ["#ffd700", "#986906", "#40494E", "#ffd700", "#19A8CB"];
  const [texture] = useState(() => new THREE.VideoTexture(video));

  // --- Lumière ---
  const lightRef = useRef();
  const [currentColor] = useState(new Color(colors1[0]));
  const [targetColor, setTargetColor] = useState(new Color(colors1[1]));
  const [colorIndex, setColorIndex] = useState(1);
  const lerpSpeed = 0.01;

  // Utiliser useRef pour l'intensité (pas de re-render)
  const intensityRef = useRef(0);
  const targetIntensityRef = useRef(0);
  const intensityLerpSpeed = 0.05;

  // Met à jour la cible quand active change
  useEffect(() => {
    console.log("Active changed to:", active);
    targetIntensityRef.current = active ? 50 : 0;
  }, [active]);

  const colorDistance = (c1, c2) => {
    const dr = c1.r - c2.r;
    const dg = c1.g - c2.g;
    const db = c1.b - c2.b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  };

  useFrame(() => {
    if (!lightRef.current) return;

    // --- interpolation de l'intensité ---
    const oldIntensity = intensityRef.current;
    intensityRef.current = THREE.MathUtils.lerp(
      intensityRef.current,
      targetIntensityRef.current,
      intensityLerpSpeed
    );
    lightRef.current.intensity = intensityRef.current;
    
    // Log pour debug (commentez après)
    // if (Math.abs(intensityRef.current - targetIntensityRef.current) > 0.1) {
    //  console.log("Intensity:", intensityRef.current.toFixed(2), "Target:", targetIntensityRef.current);
    // }

    if (active) {
      // interpolation des couleurs
      currentColor.lerp(targetColor, lerpSpeed);
      lightRef.current.color.copy(currentColor);

      if (colorDistance(currentColor, targetColor) < 0.02) {
        const nextIndex = (colorIndex + 1) % colors1.length;
        setColorIndex(nextIndex);
        setTargetColor(new Color(colors1[nextIndex]));
      }
    }
  });

  useEffect(() => {
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.format = THREE.RGBFormat;
  }, [texture]);

  return (
    <group position={position}>
      <LightWithFade
        type="rectArea"
        start={true}
        duration={2}
        finalIntensity={50}
        easing={true}
        debug={false}
        ref={lightRef}
        width={3}
        height={3}
        intensity={50}
        color={currentColor}
        position={[0, 1.2, 1]}
        rotation={[-Math.PI / 4, 0, 0]}
      />

      <mesh position={[0, 0, 0]} rotation={[0, Math.PI, 0]} scale={scale}>
        <planeGeometry args={[2.25, 4]} />
        <meshBasicMaterial map={texture} toneMapped={false} />
      </mesh>

      <mesh position={[0, 0, 0.3]}>
        <boxGeometry args={[2.35, 4.15, 0.5]} />
        <meshStandardMaterial color="black" roughness={0.7} metalness={0.2} />
      </mesh>

      <mesh position={[0, -3, 0]}>
        <boxGeometry args={[2.5, 0.5, 0.8]} />
        <meshStandardMaterial
          color="black"
          roughness={0.7}
          metalness={0.2}
          emissive="#050505"
          emissiveIntensity={0.3}
        />
      </mesh>
    </group>
  );
}